import * as stylex from '@stylexjs/stylex'
import { type PropsWithChildren } from 'react'
import { Modal, type ModalProps } from '../Modal/Modal'
import { Paper, type PaperProps } from '../Paper/Paper'
import { Slide, type SlideProps } from '../Slide/Slide'
import { zIndex } from '../tokens/zIndex.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const slideDirectionMap: Record<string, SlideProps['direction']> = {
  left: 'right',
  right: 'left',
  top: 'down',
  bottom: 'up',
}

const paperStyles = stylex.create({
  left: {
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
    height: '100%',
    maxWidth: '100vw',
  },
  right: {
    borderBottomRightRadius: 0,
    borderTopRightRadius: 0,
    height: '100%',
    maxWidth: '100vw',
  },
  top: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    maxHeight: '--webkit-fill-available',
    overflowY: 'hidden',
    width: '100%',
  },
  bottom: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    maxHeight: '--webkit-fill-available',
    overflowY: 'hidden',
    width: '100%',
  },
})

const _anchorStyles = stylex.create({
  left: {
    left: 0,
  },
  right: {
    right: 0,
  },
  top: {
    height: 'auto',
    left: 0,
    maxHeight: '100%',
    right: 0,
    top: 0,
  },
  bottom: {
    bottom: 0,
    height: 'auto',
    left: 0,
    maxHeight: '100%',
    right: 0,
    top: 'auto',
  },
})

const _baseStyles = stylex.create({
  drawer: {
    // Add iOS momentum scrolling for iOS < 13.0
    WebkitOverflowScrolling: 'touch',
    flex: '1 0 auto',
    // We disable the focus ring for mouse, touch and keyboard users.
    // At some point, it would be better to keep it for keyboard users.
    // :focus-ring CSS pseudo-class will help.
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    // temporary style
    position: 'fixed',
    zIndex: zIndex.drawer,
    height: '100%',
    overflowY: 'auto',
    top: 0,
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
  },
})

export type DrawerDirection = 'top' | 'bottom' | 'left' | 'right'

export interface DrawerProps extends Omit<ModalProps, 'xStyles'> {
  /**
   * 自訂內部子元素樣式覆寫，分為 base 與 anchor 兩組
   */
  xStyles?: {
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    anchor?: Partial<XStyles<keyof typeof _anchorStyles>>
  }
  /**
   * 抽屜滑出方向
   * @default 'left'
   */
  direction?: DrawerDirection
  /**
   * 抽屜最大高度
   */
  maxHeight?: PaperProps['maxHeight']
  /**
   * 抽屜最小高度
   */
  minHeight?: PaperProps['minHeight']
  /**
   * 抽屜高度
   */
  height?: PaperProps['height']
}

/**
 * Drawer 抽屜元件，從螢幕邊緣滑入的面板。
 * 支援 top / bottom / left / right 四個方向。
 */
export const Drawer = (props: PropsWithChildren<DrawerProps>) => {
  const {
    children,
    open = false,
    direction = 'left',
    xStyles,
    maxHeight,
    minHeight,
    height,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles?.base)
  const anchorStyles = useXStyles(_anchorStyles, xStyles?.anchor)

  return (
    <Modal
      open={open}
      {...rest}
    >
      <Slide
        in={open}
        direction={slideDirectionMap[direction]}
        rootStyle={[
          baseStyles.drawer,
          anchorStyles[direction],
        ]}
      >
        <Paper
          maxHeight={maxHeight}
          minHeight={minHeight}
          height={height}
          rootStyle={[
            baseStyles.paper,
            paperStyles[direction],
          ]}
        >
          {children}
        </Paper>
      </Slide>
    </Modal>
  )
}
